import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { SegmentData, SegmentDetailPayload, SegmentDetails } from "../../services/interface/IForecastSegmentation";
import { useSegmentationDetails } from "../../services/redux/hooks/useForecastSegmentation";
import { useEffect, useState } from "react";
import CustomLoader from "../../common/CustomLoader/CustomLoader";
import { formatNumber } from "../../utils/formatter";
import InfoOutline from "../../assets/info-outline.svg";

type Props = { segment: SegmentData, payload: SegmentDetailPayload }

const SegmentationDetails = ({ segment, payload }: Props) => {
    const { mutate, data: segmentDetails, isLoading } = useSegmentationDetails();
    const [details, setDetails] = useState<SegmentDetailPayload[]>([] as SegmentDetailPayload[]);
    const [kpi, setKpi] = useState({ outOfRange: 0, total: 0 });
    useEffect(() => {
        if (segment.Segment) {
            mutate({
                "dc": payload.dc,
                "itemId": payload.itemId,
                "ucode": payload.ucode,
                "productSegm": payload.productSegm,
                "productDesc": payload.productDesc,
                "category": payload.category,
                "subCategory": payload.subCategory,
                "vendor": payload.vendor,
                "segment": [segment.Segment],
            })
        }
    }, [segment, payload])

    useEffect(() => {
        if (segmentDetails?.sdata.length > 0) {
            if (payload.biasFlag) {
                setDetails(segmentDetails?.sdata.filter((data: SegmentDetails) => {
                    if (payload.biasFlag == 'in') {
                        return segment.MinAllowedBIAS <= data.BIAS && segment.MaxAllowedBIAS >= data.BIAS
                    } else {
                        return segment.MinAllowedBIAS > data.BIAS || segment.MaxAllowedBIAS < data.BIAS
                    }
                }))
            } else {
                setDetails([...segmentDetails?.sdata]);
            }
            setKpi({ total: segmentDetails.sdata.length, outOfRange: segmentDetails.sdata.filter((data: SegmentDetails) => segment.MinAllowedBIAS > data.BIAS || segment.MaxAllowedBIAS < data.BIAS).length })
        }

    }, [segmentDetails])

    return (<>
        {isLoading && <CustomLoader open={true} />}
        <div className="w-full flex flex-row justify-start items-center shadow rounded border-[1px] border-[#bebebe] bg-white px-[1vh] py-[1vh] text-fs20 gap-2">
            <img src={InfoOutline} alt="info"></img>
            <span>
                <strong>{kpi.outOfRange}</strong>
                <span className="opacity-40"> out of </span>
                <strong>{kpi.total}</strong> <span className="opacity-40">items are out of desired bias range of </span>
                <strong>{(segment.MinAllowedBIAS * 100).toFixed(1)}% to {(segment.MaxAllowedBIAS * 100).toFixed(1)}%</strong>
            </span>
        </div>
        <div className="w-full shadow border-[#bebebe] bg-white">
            {details && segmentDetails?.sdata.length > 0 && <div className="w-full h-full segmentation-details-table">
                <DataTable
                    className="h-full"
                    value={details}
                    stripedRows
                    showGridlines
                    scrollable
                    emptyMessage="No records found"
                    scrollHeight="60vh"
                    removableSort
                >
                    <Column
                        field="BranchId"
                        header="DC"
                    ></Column>

                    <Column
                        field="ErpItemId"
                        header="Item ID"
                    ></Column>

                    <Column
                        field="Segment"
                        header="Segment"
                    ></Column>

                    <Column
                        field="Accuracy"
                        header="Accuracy"
                        sortable
                        body={(rowdata: SegmentDetails) => {
                            return (<div className="flex justify-start items-center">{(rowdata.Accuracy * 100).toFixed(1)}%</div>)
                        }}
                    ></Column>

                    <Column
                        field="BIAS"
                        header="BIAS"
                        sortable
                        body={(rowdata: SegmentDetails) => {
                            return (<div className="flex justify-start items-center">{(rowdata.BIAS * 100).toFixed(1)}%</div>)
                        }}
                    ></Column>

                    <Column
                        header="Bias Flag"
                        body={(rowdata: SegmentDetails) => {
                            return (<div>{(rowdata.BIAS >= segment.MinAllowedBIAS && rowdata.BIAS <= segment.MaxAllowedBIAS) ? "In range" : "Not in range"}</div>)
                        }}
                    ></Column>

                    <Column
                        field="ActualW0"
                        header="Actual W0"
                        body={(rowdata: SegmentDetails) => {
                            return (
                                <div>{formatNumber(rowdata.ActualW0)}</div>
                            )
                        }}
                    ></Column>

                    <Column
                        field="ForecastW0"
                        header="Forecast W0"
                        body={(rowdata: SegmentDetails) => {
                            return (
                                <div>{formatNumber(rowdata.ForecastW0)}</div>
                            )
                        }}
                    ></Column>

                    <Column
                        field="ForecastW1"
                        header="Forecast W1"
                        body={(rowdata: SegmentDetails) => {
                            return (
                                <div>{formatNumber(rowdata.ForecastW1)}</div>
                            )
                        }}
                    ></Column>

                    <Column
                        field="ForecastW2"
                        header="Forecast W2"
                        body={(rowdata: SegmentDetails) => {
                            return (
                                <div>{formatNumber(rowdata.ForecastW2)}</div>
                            )
                        }}
                    ></Column>

                    <Column
                        field="ForecastW3"
                        header="Forecast W3"
                        body={(rowdata: SegmentDetails) => {
                            return (
                                <div>{formatNumber(rowdata.ForecastW3)}</div>
                            )
                        }}
                    ></Column>
                </DataTable>

            </div>
            }
        </div>
    </>)
}

export default SegmentationDetails;